import { Component, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { AuthService } from '../../core/auth/auth.service';
import { I18nService } from '../../shared/i18n.service';
import { TranslatePipe } from '../../shared/translate.pipe';
import { ThemeService } from '../../shared/theme.service';

@Component({
  standalone: true,
  imports: [ReactiveFormsModule, TranslatePipe],
  template: `
    <main class="auth-shell">
      <div class="auth-toolbar">
        <div class="lang-switch">
          <button type="button" [class.active]="i18n.lang() === 'en'" (click)="i18n.setLang('en')">EN</button>
          <button type="button" [class.active]="i18n.lang() === 'me'" (click)="i18n.setLang('me')">CG</button>
        </div>
        <div class="theme-switch">
          <button type="button" [class.active]="theme.mode() === 'light'" (click)="theme.setMode('light')" aria-label="Light theme">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"><circle cx="12" cy="12" r="4.2"/><path d="M12 2.5v2.4M12 19.1v2.4M4.2 4.2l1.7 1.7M18.1 18.1l1.7 1.7M2.5 12h2.4M19.1 12h2.4M4.2 19.8l1.7-1.7M18.1 5.9l1.7-1.7"/></svg>
          </button>
          <button type="button" [class.active]="theme.mode() === 'system'" (click)="theme.setMode('system')" aria-label="System theme">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4.5" width="18" height="12" rx="1.5"/><path d="M8 20h8M12 16.5V20"/></svg>
          </button>
          <button type="button" [class.active]="theme.mode() === 'dark'" (click)="theme.setMode('dark')" aria-label="Dark theme">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M20 14.5A8.5 8.5 0 1 1 9.5 4a7 7 0 0 0 10.5 10.5z"/></svg>
          </button>
        </div>
      </div>
      <section class="auth-card">
        <img class="brand" src="assets/logo.png" alt="Port of Bar">
        <h1>{{ 'auth.logIn' | t }}</h1>
        <form [formGroup]="form" (ngSubmit)="submit()">
          <label>{{ 'auth.username' | t }}<input formControlName="username" autocomplete="username"></label>
          <label>{{ 'auth.password' | t }}<input formControlName="password" type="password" autocomplete="current-password"></label>
          @if (error()) { <p class="error">{{ error() }}</p> }
          <button type="submit" [disabled]="form.invalid || loading()">{{ loading() ? ('auth.signingIn' | t) : ('auth.logIn' | t) }}</button>
        </form>
      </section>
      <div class="auth-eu-banner">
        <img src="assets/logo_all.jpg" alt="Interreg ADRION – European Union – MultiAPPRO PLUS">
      </div>
    </main>
  `
})
export class LoginPage {
  readonly loading = signal(false);
  readonly error = signal('');
  readonly form;
  readonly i18n = inject(I18nService);
  readonly theme = inject(ThemeService);

  constructor(fb: FormBuilder, private readonly auth: AuthService, private readonly router: Router) {
    this.form = fb.nonNullable.group({ username: ['', Validators.required], password: ['', Validators.required] });
  }

  submit(): void {
    if (this.form.invalid) return;
    this.loading.set(true);
    this.error.set('');
    this.auth.signIn(this.form.getRawValue()).subscribe({
      next: () => this.router.navigateByUrl('/dashboard'),
      error: err => { this.loading.set(false); this.error.set(err?.error?.message ?? this.i18n.translate('auth.failed')); }
    });
  }
}
